import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import ComplianceChecklist from './components/ComplianceChecklist';
import AuditTrail from './components/AuditTrail';
import RegulatoryCalendar from './components/RegulatoryCalendar';
import ComplianceScoring from './components/ComplianceScoring';
import DocumentManagement from './components/DocumentManagement';
import AlertConfiguration from './components/AlertConfiguration';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ComplianceDashboard = () => {
  const [activeView, setActiveView] = useState('overview');

  const complianceStats = {
    overallScore: 87,
    activeAlerts: 4,
    pendingTasks: 6,
    documentsExpiring: 3,
    upcomingDeadlines: 2
  };

  const quickActions = [
    {
      id: 1,
      title: 'Generate Compliance Report',
      description: 'Create comprehensive compliance summary',
      icon: 'FileText',
      action: 'generate_report',
      priority: 'high'
    },
    {
      id: 2,
      title: 'Review Pending Tasks',
      description: 'Check items requiring attention',
      icon: 'CheckSquare',
      action: 'review_tasks',
      priority: 'medium'
    },
    {
      id: 3,
      title: 'Update Risk Thresholds',
      description: 'Modify compliance monitoring limits',
      icon: 'Settings',
      action: 'update_thresholds',
      priority: 'low'
    },
    {
      id: 4,
      title: 'Schedule Audit',
      description: 'Plan upcoming compliance audit',
      icon: 'Calendar',
      action: 'schedule_audit',
      priority: 'medium'
    }
  ];

  const viewOptions = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'checklist', label: 'Checklist', icon: 'CheckSquare' },
    { id: 'audit', label: 'Audit Trail', icon: 'Activity' },
    { id: 'calendar', label: 'Calendar', icon: 'Calendar' },
    { id: 'scoring', label: 'Scoring', icon: 'Award' },
    { id: 'documents', label: 'Documents', icon: 'FolderOpen' },
    { id: 'alerts', label: 'Alerts', icon: 'Bell' }
  ];

  const getStatColor = (value, type) => {
    switch (type) {
      case 'score':
        if (value >= 90) return 'text-success';
        if (value >= 80) return 'text-accent';
        if (value >= 70) return 'text-warning';
        return 'text-error';
      case 'alerts':
        if (value === 0) return 'text-success';
        if (value <= 2) return 'text-warning';
        return 'text-error';
      case 'tasks':
        if (value === 0) return 'text-success';
        if (value <= 3) return 'text-warning';
        return 'text-error';
      default:
        return 'text-foreground';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-error bg-error/5';
      case 'medium': return 'border-l-warning bg-warning/5';
      case 'low': return 'border-l-accent bg-accent/5';
      default: return 'border-l-muted-foreground bg-muted/5';
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card rounded-lg border border-border p-4 shadow-subtle">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Award" size={20} className={getStatColor(complianceStats?.overallScore, 'score')} />
            <span className="text-xs text-muted-foreground">Overall</span>
          </div>
          <div className={`text-2xl font-bold ${getStatColor(complianceStats?.overallScore, 'score')}`}>
            {complianceStats?.overallScore}
          </div>
          <div className="text-sm text-muted-foreground">Compliance Score</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 shadow-subtle">
          <div className="flex items-center justify-between mb-2">
            <Icon name="AlertTriangle" size={20} className={getStatColor(complianceStats?.activeAlerts, 'alerts')} />
            <span className="text-xs text-muted-foreground">Active</span>
          </div>
          <div className={`text-2xl font-bold ${getStatColor(complianceStats?.activeAlerts, 'alerts')}`}>
            {complianceStats?.activeAlerts}
          </div>
          <div className="text-sm text-muted-foreground">Alerts</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 shadow-subtle">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckSquare" size={20} className={getStatColor(complianceStats?.pendingTasks, 'tasks')} />
            <span className="text-xs text-muted-foreground">Pending</span>
          </div>
          <div className={`text-2xl font-bold ${getStatColor(complianceStats?.pendingTasks, 'tasks')}`}>
            {complianceStats?.pendingTasks}
          </div>
          <div className="text-sm text-muted-foreground">Tasks</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 shadow-subtle">
          <div className="flex items-center justify-between mb-2">
            <Icon name="FileText" size={20} className="text-warning" />
            <span className="text-xs text-muted-foreground">Expiring</span>
          </div>
          <div className="text-2xl font-bold text-warning">
            {complianceStats?.documentsExpiring}
          </div>
          <div className="text-sm text-muted-foreground">Documents</div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 shadow-subtle">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={20} className="text-error" />
            <span className="text-xs text-muted-foreground">Upcoming</span>
          </div>
          <div className="text-2xl font-bold text-error">
            {complianceStats?.upcomingDeadlines}
          </div>
          <div className="text-sm text-muted-foreground">Deadlines</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-card rounded-lg border border-border shadow-subtle">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground mb-2">Quick Actions</h2>
          <p className="text-sm text-muted-foreground">Common compliance management tasks</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quickActions?.map(action => (
              <div key={action?.id} className={`border-l-4 p-4 rounded-r-lg cursor-pointer hover:shadow-subtle transition-shadow duration-200 ${getPriorityColor(action?.priority)}`}>
                <div className="flex items-start gap-3">
                  <Icon name={action?.icon} size={20} className="text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground mb-1">{action?.title}</h3>
                    <p className="text-sm text-muted-foreground">{action?.description}</p>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border shadow-subtle">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground mb-2">Recent Compliance Activity</h3>
            <p className="text-sm text-muted-foreground">Latest compliance-related actions</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground">EUA Registry verification completed</p>
                  <p className="text-xs text-muted-foreground">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
                  <Icon name="Clock" size={16} className="text-warning" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground">MiFID II reporting deadline approaching</p>
                  <p className="text-xs text-muted-foreground">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center">
                  <Icon name="Upload" size={16} className="text-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground">Risk management policy updated</p>
                  <p className="text-xs text-muted-foreground">1 day ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-subtle">
          <div className="p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground mb-2">Compliance Health</h3>
            <p className="text-sm text-muted-foreground">Key performance indicators</p>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">EUA Compliance</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-muted rounded-full h-2">
                    <div className="bg-success rounded-full h-2" style={{ width: '92%' }} />
                  </div>
                  <span className="text-sm font-medium text-success">92%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Oil Futures</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-muted rounded-full h-2">
                    <div className="bg-accent rounded-full h-2" style={{ width: '85%' }} />
                  </div>
                  <span className="text-sm font-medium text-accent">85%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Risk Management</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-muted rounded-full h-2">
                    <div className="bg-warning rounded-full h-2" style={{ width: '78%' }} />
                  </div>
                  <span className="text-sm font-medium text-warning">78%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Documentation</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-muted rounded-full h-2">
                    <div className="bg-success rounded-full h-2" style={{ width: '94%' }} />
                  </div>
                  <span className="text-sm font-medium text-success">94%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return renderOverview();
      case 'checklist':
        return <ComplianceChecklist />;
      case 'audit':
        return <AuditTrail />;
      case 'calendar':
        return <RegulatoryCalendar />;
      case 'scoring':
        return <ComplianceScoring />;
      case 'documents':
        return <DocumentManagement />;
      case 'alerts':
        return <AlertConfiguration />;
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Compliance Dashboard</h1>
                <p className="text-muted-foreground">
                  Centralized regulatory tracking and audit trail management for EUA and Oil Futures trading operations
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" iconName="RefreshCw" iconPosition="left" size="sm">
                  Refresh Data
                </Button>
                <Button variant="default" iconName="Download" iconPosition="left" size="sm">
                  Export Report
                </Button>
              </div>
            </div>

            {/* View Navigation */}
            <div className="flex flex-wrap gap-2 border-b border-border pb-4">
              {viewOptions?.map(option => (
                <button
                  key={option?.id}
                  onClick={() => setActiveView(option?.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200
                    ${activeView === option?.id
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                >
                  <Icon name={option?.icon} size={16} />
                  <span>{option?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;